import React, { useMemo, useState } from "react";
import {
  ActionIcon,
  Box,
  Card,
  Flex,
  Group,
  ScrollArea,
  Stack,
  Text,
  useMantineTheme,
} from "@mantine/core";
import { useTranslation } from "react-i18next";
import { IconPlus } from "../../../shared/svg/AddIcon";
import { CalendarAddTimestamp } from "../calendar-timestamps/CalendarAddTimestamp";
import useDevelopmentStore from "../../../store/useDevelopmentStore";
import { CalendarEditTimestamp } from "../calendar-timestamps/CalendarEditTimestamp";
import { normalizeGoalTimestamp } from "../../../widgets/development-plan/Calendar";
import { GoalTimestamp } from "../../../types/api";
import BotMessageSquareIcon from "../../../shared/svg/BotMessage";
import { AITooltip } from "../../../utils/components/AITooltip";

interface Props {
  goalId: number | null;
}

export const GoalTasks: React.FC<Props> = ({ goalId }) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isEditTaskOpen, setIsEditTaskOpen] = useState(false);
  const [editTask, setEditTask] = useState<GoalTimestamp | null>(null);
  const theme = useMantineTheme();

  const { goals } = useDevelopmentStore();

  const normalizedTask = useMemo(() => {
    return editTask ? normalizeGoalTimestamp(editTask) : null;
  }, [editTask]);

  const goal = goals.find((g) => g.id === goalId) || null;

  return (
    <Box maw="100%">
      <Flex align="center" gap={10}>
        <Group gap={0} align="center" wrap="nowrap">
          <Text size="md" fw={600} style={{ color: "#333" }}>
            {t("goal_tasks")}
          </Text>
        </Group>

        <ActionIcon
          variant="subtle"
          onClick={() => {
            setIsOpen(true);
          }}
          style={{
            backgroundColor: "#f0f0f0",
            padding: "8px",
            borderRadius: "50%",
            boxShadow: "0 0 4px rgba(0, 0, 0, 0.1)",
          }}
        >
          <IconPlus />
        </ActionIcon>
      </Flex>

      <ScrollArea
        style={{
          marginTop: goal !== null && goal.tasks.length > 0 ? 20 : 0,
          maxHeight: 300,
          overflowY: "auto",
        }}
      >
        <Stack gap={5} style={{ width: "100%" }}>
          {goal?.tasks?.map((task) => (
            <Card
              key={task.id}
              radius="md"
              withBorder
              style={{
                cursor: "pointer",
                width: "100%",
                margin: 0,
                padding: "10px 14px",
                backgroundColor: task.display_color_rgba,
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                gap: 10,
              }}
              onClick={() => {
                setEditTask(task);
                setIsEditTaskOpen(true);
              }}
            >
              <Flex
                align="center"
                gap={8}
                style={{ overflow: "hidden", flex: 1 }}
              >
                {task.autogenerated && (
                  <AITooltip
                    icon={
                      <Box
                        ml={4}
                        w={28}
                        h={28}
                        style={{
                          borderRadius: "50%",
                          backgroundColor: "white",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                        }}
                      >
                        <BotMessageSquareIcon
                          size={16}
                          color={theme.colors.violet[7]}
                        />
                      </Box>
                    }
                    title={t("generated_by_ai")}
                    zIndex={301}
                  />
                )}
                <Text size="md" fw={500} style={{ wordBreak: "break-word" }}>
                  {task.title}
                </Text>
              </Flex>
            </Card>
          ))}
        </Stack>
      </ScrollArea>

      <CalendarAddTimestamp
        opened={isOpen}
        onClose={() => setIsOpen(false)}
        goalId={goal?.id ?? null}
      />

      <CalendarEditTimestamp
        opened={isEditTaskOpen}
        taskToEdit={normalizedTask}
        onClose={() => {
          setIsEditTaskOpen(false);
          setEditTask(null);
        }}
        goalId={goalId ?? null}
      />
    </Box>
  );
};
